import SettingConfig from '@module/config/setting';
import { itemsPerPage } from '@module/constants/pagenation';
import useGetLoginInfoQuery from '@module/helpers/auth/useGetLoginInfoQuery';
import { aspCond } from '@module/helpers/conditions/condition';
import { useBjInfoQuery } from '@module/queries/member/memberQueryHooks';
import { useBjNoticeDeleteMutation, useFetchBjNoticeListQuery } from '@module/queries/notice/noticeQueryHooks';
import useModalStore from '@module/stores/useModalStore';
import { MODAL_TYPE_PC } from '@module/types/modal/type';
import catchAllErrors from '@module/utils/catchAllErrors';
import { keepPreviousData } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useDeleteVoteMutation } from '../../../queries/vote/voteQueryHooks';
function useNoticeListViewModel(channelId: string) {
  const {
    loginInfo,
    userInfo,
    isLogin
  } = useGetLoginInfoQuery();
  const isMyChannel = channelId === userInfo?.id;
  const pushModal = useModalStore(state => state.pushModal);
  const popModal = useModalStore(state_0 => state_0.popModal);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const currentPageFirstOffset = (currentPage - 1) * itemsPerPage;
  const {
    data: bjInfoData
  } = useBjInfoQuery({
    userId: channelId,
    info: 'media fanGrade adminBjNotice exchange checkEnter bookmark'
  });
  const {
    data: noticeList,
    refetch: refetchNoticeList,
    isFetching: isNoticeListFetching,
    isLoading: isNoticeListLoading
  } = useFetchBjNoticeListQuery({
    userIdx: bjInfoData?.bjInfo.idx || 0,
    offset: currentPageFirstOffset,
    limit: itemsPerPage
  }, {
    placeholderData: keepPreviousData,
    enabled: !!bjInfoData
  });
  const {
    mutate: deleteNotice
  } = useBjNoticeDeleteMutation({
    onSuccess: () => {
      refetchNoticeList();
    },
    onError: err => catchAllErrors(err.message, () => {
      pushModal({
        id: 'useNoticeListViewModel - 1',
        type: 'CONFIRM_MODAL',
        options: {
          title: '요청실패',
          description: err.message,
          onClose: popModal
        }
      });
    })
  });
  const {
    mutate: deleteVote
  } = useDeleteVoteMutation({
    onSuccess: data => {
      pushModal({
        id: 'useNoticeListViewModel - 2',
        type: 'CONFIRM_MODAL',
        options: {
          description: data.message
        }
      });
      refetchNoticeList();
    },
    onError: err_0 => catchAllErrors(err_0.message, () => {
      pushModal({
        id: 'useNoticeListViewModel - 3 ',
        type: 'CONFIRM_MODAL',
        options: {
          title: '요청실패',
          description: err_0.message,
          onClose: popModal
        }
      });
    })
  });
  const showNoticeEditModal = useCallback((noticeIdx: number, contents: string, image?: string, isGlobal?: boolean, isTop?: boolean) => {
    if (!isLogin) {
      if (aspCond.if('new-asp')) {
        SettingConfig.aspUtils?.goLogin();
        return;
      }
      pushModal({
        id: 'useNoticeListViewModel - 4',
        type: MODAL_TYPE_PC.AUTH_MODAL
      });
      return;
    }
    if (!userInfo) return;
    pushModal({
      id: 'useNoticeListViewModel - 5',
      type: MODAL_TYPE_PC.NOTICE_WRITE_MODAL,
      options: {
        mode: 'edit',
        userIdx: userInfo.idx,
        noticeIdx,
        contents,
        image,
        isGlobal,
        isTop,
        onSuccess: refetchNoticeList
      }
    });
  }, [isLogin, userInfo, pushModal, refetchNoticeList]);
  const showNoticeDeleteConfirmModal = useCallback((noticeIdx_0: number) => {
    if (!isLogin) {
      if (aspCond.if('new-asp')) {
        SettingConfig.aspUtils?.goLogin();
        return;
      }
      pushModal({
        id: 'useNoticeListViewModel - 6',
        type: MODAL_TYPE_PC.AUTH_MODAL
      });
      return;
    }
    pushModal({
      id: 'useNoticeListViewModel - 7',
      type: MODAL_TYPE_PC.CONFIRM_CANCEL_MODAL,
      options: {
        description: '선택한 알림을 삭제하시겠습니까?',
        onClose: () => {
          popModal();
        },
        onClick: () => {
          deleteNotice({
            idx: noticeIdx_0
          });
          popModal();
        }
      }
    });
  }, [isLogin, deleteNotice, popModal, pushModal]);
  const showDeleteVoteConfirmModal = useCallback((voteIdx: number) => {
    if (!isLogin) {
      if (aspCond.if('new-asp')) {
        SettingConfig.aspUtils?.goLogin();
        return;
      }
      pushModal({
        id: 'useNoticeListViewModel - 8',
        type: MODAL_TYPE_PC.AUTH_MODAL
      });
      return;
    }
    pushModal({
      id: 'useNoticeListViewModel - 9',
      type: MODAL_TYPE_PC.CONFIRM_CANCEL_MODAL,
      options: {
        description: '선택한 투표를 삭제하시겠습니까?',
        onClose: () => {
          popModal();
        },
        onClick: () => {
          deleteVote({
            voteIdx
          });
          popModal();
        }
      }
    });
  }, [deleteVote, isLogin, popModal, pushModal]);
  const handleReportClick = useCallback((boardIdx: number, boardContents: string) => {
    if (!isLogin) {
      if (aspCond.if('new-asp')) {
        SettingConfig.aspUtils?.goLogin();
        return;
      }
      pushModal({
        id: 'useNoticeListViewModel - 10',
        type: MODAL_TYPE_PC.AUTH_MODAL
      });
      return;
    }
    if (!bjInfoData) return;
    pushModal({
      id: 'useNoticeListViewModel - 11',
      type: MODAL_TYPE_PC.REPORT_MODAL,
      options: {
        type: 'bjNotice',
        boardIdx,
        boardContents,
        toUserIdx: bjInfoData.bjInfo.idx,
        toUserNickName: bjInfoData.bjInfo.nick,
        toUserId: bjInfoData.bjInfo.id
      }
    });
  }, [bjInfoData, pushModal, isLogin]);
  const showVoteModal = useCallback((voteIdx_0?: number) => {
    if (!isLogin) {
      if (aspCond.if('new-asp')) {
        SettingConfig.aspUtils?.goLogin();
        return;
      }
      pushModal({
        id: 'useNoticeListViewModel - 12',
        type: MODAL_TYPE_PC.AUTH_MODAL
      });
      return;
    }
    pushModal({
      id: 'useNoticeListViewModel - 13',
      type: MODAL_TYPE_PC.VOTE_MODAL,
      options: {
        type: 'channel',
        voteIdx: voteIdx_0,
        isMyChannel
      }
    });
  }, [isLogin, pushModal]);
  const showNoticeWriteModal = useCallback(() => {
    if (!isLogin) {
      if (aspCond.if('new-asp')) {
        SettingConfig.aspUtils?.goLogin();
        return;
      }
      pushModal({
        id: 'useNoticeListViewModel - 14',
        type: MODAL_TYPE_PC.AUTH_MODAL
      });
      return;
    }
    if (!userInfo) return;
    if (userInfo.authYN === 'N') {
      pushModal({
        id: 'useNoticeListViewModel - 15',
        type: MODAL_TYPE_PC.CONFIRM_CANCEL_MODAL,
        options: {
          description: '본인인증이 필요합니다.\n 본인인증 후 이용해 주세요',
          onClose: () => {
            popModal();
          },
          onClick: () => {
            popModal();
            pushModal({
              id: 'useNoticeListViewModel - 16',
              type: MODAL_TYPE_PC.IDENTITY_VERIFICATION_MODAL
            });
          }
        }
      });
    } else {
      pushModal({
        id: 'useNoticeListViewModel - 17',
        type: MODAL_TYPE_PC.NOTICE_WRITE_MODAL,
        options: {
          mode: 'write',
          userIdx: userInfo.idx,
          onSuccess: refetchNoticeList
        }
      });
    }
  }, [userInfo, isLogin, pushModal, refetchNoticeList]);
  const showNoticInfoModal = useCallback((imageUrl: string, contents_0: string, isGlobal_0?: boolean) => {
    pushModal({
      id: 'useNoticeListViewModel - 18',
      type: MODAL_TYPE_PC.NOTICE_INFO_MODAL,
      options: {
        imageUrl,
        contents: contents_0,
        isGlobal: isGlobal_0
      }
    });
  }, [pushModal]);
  const isListEmpty = !isNoticeListFetching && !noticeList?.page.total;
  return {
    loginInfo,
    noticeList,
    isMyChannel,
    currentPage,
    setCurrentPage,
    isNoticeListFetching,
    isNoticeListLoading,
    isListEmpty,
    showVoteModal,
    showNoticeWriteModal,
    showNoticeEditModal,
    showNoticeDeleteConfirmModal,
    showDeleteVoteConfirmModal,
    showNoticInfoModal,
    handleReportClick
  };
}
export default useNoticeListViewModel;